import onInitialTableLoadComplete from 'owa-mail-table-loading-actions/lib/actions/onInitialTableLoadComplete';
import loadInitialRowsFromServer from 'owa-mail-list-actions/lib/actions/table/loadInitialRowsFromServer';
import { singleSelectRow } from 'owa-mail-actions/lib/singleSelectRow';
import MailListItemSelectionSource from 'owa-mail-store/lib/store/schema/MailListItemSelectionSource';
import { INITIAL_LOAD_ROW_COUNT } from 'owa-mail-triage-common';
import {
    getGQLPagingInfoForQueryRows,
    getGQLPagingInfoForQueryRowsForJumpTo,
    resetTableViewPropertiesMutator,
    isMailListConversationView,
} from 'owa-mail-list-store';
import type { MailFolderTableQuery, TableView } from 'owa-mail-list-store';
import { logUsage } from 'owa-analytics';
import { getAggregationBucket } from 'owa-analytics-actions';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import { createMailListLoadDatapoint } from 'owa-mail-logging/lib/utils/mailListLoad/mailListLoadDatapointUtils';
import { markMailListLoadReadyToRender } from 'owa-mail-logging/lib/utils/mailListLoad/mailListLoadDatapointGetterAndSetter';
import getJumpToSortInfo from '../utils/getJumpToSortInfo';
import executeClientJumpTo from './executeClientJumpTo';

export type JumpToEntryPoint = 'HeaderButton' | 'SortAutoOpen' | 'TypedInList';

export default function executeJumpTo(
    tableView: TableView,
    matchingString: string,
    entryPoint: JumpToEntryPoint = 'HeaderButton'
): void {
    // We don't want to jump in the list while the tableView is loading because that can cause unexpected behavior of rows
    // being fetched from the previous table getting appended to the jump to results.
    if (tableView.isLoading) {
        return;
    }

    const sortInfo = getJumpToSortInfo(tableView);
    if (!sortInfo) {
        return;
    }

    const isConversationView = isMailListConversationView(tableView);
    const isClientSideJumpTo = !!sortInfo.clientSortOrder;

    logUsage('JumpTo_Triggered', getMailboxInfo(tableView), {
        sortColumn: sortInfo.sortColumn,
        sortOrder: sortInfo.sortDirection,
        isConversationView,
        isClientSideJumpTo,
        entryPoint,
        numRowKeysInTable: getAggregationBucket({
            value: tableView.rowKeys.length,
            buckets: [10, 25, 50, 100, 250, 500],
        }),
    });

    if (sortInfo.clientSortOrder) {
        executeClientJumpTo(tableView, matchingString, sortInfo.clientSortOrder);
        return;
    }

    const tableQuery = tableView.tableQuery as MailFolderTableQuery;
    const customPagingInfo = getGQLPagingInfoForQueryRowsForJumpTo(
        INITIAL_LOAD_ROW_COUNT,
        isConversationView,
        matchingString,
        tableQuery.sortBy
    );

    resetTableViewPropertiesMutator(tableView);

    // Jump-to bypasses loadInitialMailTable, so create the MailListLoad datapoint here.
    // The reset above clears rows and drops the list to its loading state, so the
    // VirtualizedList remounts on completion and ends this datapoint via the render layer.
    const mailListLoadId = createMailListLoadDatapoint('JumpToNavigation', tableView);

    // If the jump-to restriction matches nothing (e.g., the user jumped past the end of
    // the list), the server returns 0 rows but still reports the folder's totalRowsInView.
    // Without intervention, the empty-state view would render even though the folder has
    // rows. Fall back to loading from the end of the list so the user lands at the
    // closest existing position. The fallback skips the original onInitialTableLoadComplete
    // so isLoading stays true and the empty state never flashes.
    const onJumpToLoadComplete = (
        completedTableView: TableView,
        isSuccessResponseClass: boolean,
        responseCode: string,
        isTablePrefetched: boolean
    ): void => {
        if (
            isSuccessResponseClass &&
            completedTableView.rowKeys.length === 0 &&
            completedTableView.totalRowsInView > 0
        ) {
            const onFallbackLoadComplete = (
                tv: TableView,
                fallbackIsSuccess: boolean,
                fallbackResponseCode: string,
                fallbackIsPrefetched: boolean
            ): void => {
                if (fallbackIsSuccess && tv.rowKeys.length > 0) {
                    singleSelectRow(
                        tv,
                        tv.rowKeys[tv.rowKeys.length - 1],
                        false /*isUserNavigation*/,
                        MailListItemSelectionSource.Auto
                    );
                }

                onInitialTableLoadComplete(
                    tv,
                    fallbackIsSuccess,
                    fallbackResponseCode,
                    fallbackIsPrefetched
                );
                markMailListLoadReadyToRender(tv.id, mailListLoadId);
            };

            loadInitialRowsFromServer(
                completedTableView,
                false /*isTablePrefetched*/,
                onFallbackLoadComplete,
                undefined /*initialSessionData*/,
                false /*skipGqlTimings*/,
                getGQLPagingInfoForQueryRows(
                    INITIAL_LOAD_ROW_COUNT,
                    isConversationView,
                    undefined /*instanceKey*/,
                    'End'
                )
            );
            return;
        }

        onInitialTableLoadComplete(
            completedTableView,
            isSuccessResponseClass,
            responseCode,
            isTablePrefetched
        );
        markMailListLoadReadyToRender(completedTableView.id, mailListLoadId);
    };

    loadInitialRowsFromServer(
        tableView,
        false /*isTablePrefetched*/,
        onJumpToLoadComplete,
        undefined /*initialSessionData*/,
        false /*skipGqlTimings*/,
        customPagingInfo
    );
}
